import type { SourceId } from '../../../../constants';
import type {
  DataServiceMediaDocumentType,
  DataServiceMediaImageType,
  DataServiceMediaValueType,
  DataServiceMediaVideoType
} from '../../../types';
import type {
  GalleryMediaValue,
  LinkMediaValue,
  MediaValue,
  PictureValueType
} from '../../sources';
import { getMediaWithProxy as getMediaWithPhosphorProxy } from '../getMediaWithProxy';
import { getVideoProxyfied } from '../getVideoProxyfied';
import { matchMediaType } from '../matchMediaType';

type DataServicePostMediaValue = Exclude<
  MediaValue<PictureValueType[] | string>,
  LinkMediaValue
>;

function getImageSizesArray<T extends Partial<DataServiceMediaValueType>>({
  original,
  standard,
  thumbnail,
  cover
}: T) {
  if (cover) {
    return getImageSizesArray(cover);
  }

  return [original, standard, thumbnail].reduce<PictureValueType[]>(
    (acc, pictureValue) => {
      if (!pictureValue?.url) {
        return acc;
      }

      acc.push({
        ...pictureValue,
        url: getMediaWithPhosphorProxy(pictureValue.url)
      });

      return acc;
    },
    []
  );
}

type GetDataServicePostMediaParams = {
  attachments: DataServiceMediaValueType[];
  platform: SourceId;
  shouldUseVideoProxy?: boolean;
  widgetToken?: string;
};

export function getDataServicePostMedia({
  attachments,
  platform,
  shouldUseVideoProxy = false,
  widgetToken = ''
}: GetDataServicePostMediaParams): DataServicePostMediaValue | undefined {
  if (!attachments?.length) {
    return undefined;
  }

  if (attachments.length > 1) {
    const [firstAttachment] = attachments;

    const attachmentSizes = getImageSizesArray(firstAttachment);

    if (!attachmentSizes.length) {
      return;
    }

    const thumbnail = attachmentSizes.find(({ url }) => !!url)!.url;

    const items = attachments
      .map((attachment) =>
        getDataServicePostMedia({
          attachments: [attachment],
          platform,
          shouldUseVideoProxy,
          widgetToken
        })
      )
      .filter(Boolean) as GalleryMediaValue['items'];

    return {
      type: 'gallery',
      items,
      thumbnail
    };
  }

  const [{ type, ...mediaProps }] = attachments;

  const mediaType = matchMediaType(type);

  if (mediaType === 'image') {
    const imageProps = mediaProps as DataServiceMediaImageType;
    const src = getImageSizesArray(imageProps);

    if (!src.length) {
      return;
    }

    return {
      type: 'image',
      src
    };
  }

  if (mediaType === 'video') {
    const { url, cover } = mediaProps as DataServiceMediaVideoType;

    const coverSizes = getImageSizesArray(cover || {});

    if (!coverSizes.length || !url) {
      return;
    }

    const thumbnail = coverSizes.find(({ url }) => !!url)!.url;

    const src = shouldUseVideoProxy
      ? getVideoProxyfied(url, widgetToken)
      : getMediaWithPhosphorProxy(url);

    return {
      type: 'video',
      src,
      thumbnail
    };
  }

  if (type === 'article') {
    const { url, image, title } = mediaProps as DataServiceMediaDocumentType;

    const imageSizes = image ? getImageSizesArray(image) : [];

    if (!imageSizes.length || !url) {
      return;
    }

    const [{ url: src }] = imageSizes;

    return {
      type: 'link',
      description: '',
      title: title || '',
      url: url ?? '',
      src
    };
  }

  if (mediaType === 'gallery') {
    const { images } = mediaProps as DataServiceMediaDocumentType;

    const imageSizes = getImageSizesArray(images[0]);

    if (!imageSizes.length) {
      return;
    }

    const thumbnail = imageSizes.find(({ url }) => !!url)!.url;

    return {
      type: 'gallery',
      items: images.map((image) => ({
        type: 'image',
        src: getImageSizesArray(image)
      })),
      thumbnail
    };
  }

  return;
}
